import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowRight, Zap } from 'lucide-react';

/* ─── Data ─────────────────────────────────────────────────── */
const PRIMARY = [
  {
    title: 'FIXFLOW',
    subtitle: 'AI-Powered Code & Data Dependency Failure Diagnosis',
    description:
      'Intelligent GitHub PR guard and root-cause engine. Maps a repo\'s full dependency graph — React, NestJS, TypeORM, SQL migrations, dbt models — straight from source, runs deterministic contract checks, then posts a file-specific fix to the PR.',
    tags: ['FastAPI', 'MongoDB', 'Redis', 'Next.js', 'TypeScript', 'Docker'],
    link: 'https://github.com/Krishna41357/FixFlow',
    status: 'live',
    metrics: ['4 contract checks', 'Sub-sec PR comment'],
    accent: '#7c3aed',
    abbr: 'FF',
  },
  {
    title: 'KS CODE',
    subtitle: 'Real-Time Collaborative Code Editor',
    description:
      'Monaco-powered collaborative editor for pair programming and remote debugging. Every keystroke and cursor syncs over WebSockets in real time, with built-in WebRTC video and Groq-powered AI debug assist.',
    tags: ['WebRTC', 'WebSockets', 'React', 'Node.js', 'Groq LLM'],
    link: 'https://ks-code.vercel.app/',
    status: 'live',
    metrics: ['10+ concurrent users', 'sub-100ms sync'],
    accent: '#2563eb',
    abbr: 'KS',
  },
];

const SECONDARY = {
  title: 'KS RAG',
  subtitle: 'Retrieval-Augmented Generation Chatbot',
  description:
    'RAG chatbot using vector embeddings and cosine similarity, reaching 95% retrieval accuracy on 100-page PDFs with sub-2s response times.',
  tags: ['Python', 'FastAPI', 'Next.js', 'MongoDB', 'OpenAI'],
  link: 'https://ks-rag.vercel.app/',
  status: 'live',
  metrics: ['95% retrieval accuracy', 'sub-2s response'],
};

const TERTIARY = [
  {
    title: 'Finance-Buddy',
    role: 'AI financial literacy platform with paper trading & research agents',
    tags: ['React', 'Python', 'Redis', 'AWS'],
    link: 'https://finance-buddy-lemon.vercel.app/',
    status: 'live',
  },
  {
    title: 'Nitai Gems',
    role: 'Production e-commerce — serverless Hono on Cloudflare Workers',
    tags: ['Hono', 'Cloudflare', 'TypeScript'],
    link: 'https://www.nitaigems.com',
    status: 'live',
  },
  {
    title: 'Vaidya',
    role: 'AI crop disease detection with fine-tuned ML models on image datasets',
    tags: ['AI/ML', 'React', 'Express', 'MongoDB'],
    link: 'https://drive.google.com/drive/folders/13dDAKWoIeXlYifJZBaPm_7oICd2UxDAM',
    status: 'completed',
  },
  {
    title: 'Events Website',
    role: 'Scroll-animated event showcase with GSAP & React',
    tags: ['React', 'GSAP', 'Vite'],
    link: 'https://dronewebsite-steel.vercel.app/',
    status: 'live',
  },
  {
    title: 'Kanban Board',
    role: 'Multi-user task management with real-time state sync',
    tags: ['ReactJS', 'State Management'],
    link: 'https://kanbanboard-roan.vercel.app/',
    status: 'completed',
  },
];

/* ─── Helpers ───────────────────────────────────────────────── */
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] },
});

function LiveDot() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold font-mono bg-green-50 text-green-700 border border-green-200">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
      LIVE
    </span>
  );
}

/* ─── Primary card ──────────────────────────────────────────── */
function PrimaryCard({ p, delay }) {
  return (
    <motion.div
      {...fadeUp(delay)}
      className="group flex flex-col bg-white border border-black/10 rounded-2xl overflow-hidden shadow-[3px_3px_0px_0px_rgba(0,0,0,0.08)] hover:shadow-[5px_5px_0px_0px_rgba(0,0,0,0.12)] transition-all duration-200"
    >
      {/* Coloured header band */}
      <div
        className="flex items-end justify-between px-5 pt-5 pb-4"
        style={{ background: `${p.accent}12` }}
      >
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center text-white text-sm font-black shadow-[2px_2px_0px_0px_rgba(0,0,0,0.2)]"
          style={{ background: p.accent }}
        >
          {p.abbr}
        </div>
        <LiveDot />
      </div>

      {/* Body */}
      <div className="flex flex-col flex-1 px-5 py-4">
        <h3 className="text-lg font-black text-black tracking-tight mb-0.5">{p.title}</h3>
        <p className="text-[11px] font-bold text-black/40 mb-3 tracking-tight">{p.subtitle}</p>

        <p className="text-[12px] text-gray-600 leading-relaxed mb-4 flex-1">{p.description}</p>

        {/* Metrics */}
        <div className="flex gap-3 mb-4 flex-wrap">
          {p.metrics.map(m => (
            <span
              key={m}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold font-mono bg-black/4 text-black/60 border border-black/8"
            >
              <Zap size={8} className="text-purple-500" />
              {m}
            </span>
          ))}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {p.tags.map(tag => (
            <span
              key={tag}
              className="px-2 py-0.5 text-[9px] font-mono uppercase tracking-wide text-black/45 bg-black/4 rounded-md border border-black/8"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* CTA */}
        <a
          href={p.link}
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-black text-white text-[11px] font-black border border-black hover:bg-purple-600 hover:border-purple-600 transition-colors duration-200"
        >
          View Project <ArrowUpRight size={12} />
        </a>
      </div>
    </motion.div>
  );
}

/* ─── Secondary card ────────────────────────────────────────── */
function SecondaryCard({ p }) {
  return (
    <motion.div
      {...fadeUp(0.1)}
      className="group bg-white border border-black/10 rounded-2xl overflow-hidden shadow-[3px_3px_0px_0px_rgba(0,0,0,0.06)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)] transition-all duration-200"
    >
      <div className="flex flex-col sm:flex-row sm:items-stretch">
        {/* Left accent */}
        <div className="w-full sm:w-1 sm:h-auto h-1 bg-purple-600 shrink-0" />

        {/* Content */}
        <div className="flex-1 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-base font-black text-black">{p.title}</h3>
              <LiveDot />
            </div>
            <p className="text-[11px] font-bold text-black/40 mb-2">{p.subtitle}</p>
            <p className="text-[12px] text-gray-600 leading-relaxed mb-3">{p.description}</p>
            <div className="flex flex-wrap gap-1.5">
              {p.tags.map(tag => (
                <span
                  key={tag}
                  className="px-2 py-0.5 text-[9px] font-mono uppercase tracking-wide text-black/45 bg-black/4 rounded-md border border-black/8"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Right: metrics + CTA */}
          <div className="flex flex-row sm:flex-col items-center sm:items-end gap-3 shrink-0">
            <div className="hidden sm:flex flex-col gap-1.5">
              {p.metrics.map(m => (
                <span
                  key={m}
                  className="px-2.5 py-1 rounded-lg text-[10px] font-bold font-mono bg-black/4 text-black/55 border border-black/8 whitespace-nowrap text-right"
                >
                  {m}
                </span>
              ))}
            </div>
            <a
              href={p.link}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 px-4 py-2 rounded-xl bg-black text-white text-[11px] font-black hover:bg-purple-600 transition-colors duration-200 whitespace-nowrap"
            >
              View <ArrowUpRight size={11} />
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Tertiary row ──────────────────────────────────────────── */
function TertiaryRow({ p, delay }) {
  return (
    <motion.a
      href={p.link}
      target="_blank"
      rel="noreferrer"
      {...fadeUp(delay)}
      className="group flex items-center gap-4 py-3.5 border-b border-black/8 hover:bg-black/[0.015] transition-colors duration-150 px-1 -mx-1 rounded-sm"
    >
      {/* Status dot */}
      <span className={`w-2 h-2 rounded-full shrink-0 ${p.status === 'live' ? 'bg-green-400' : 'bg-black/20'}`} />

      <div className="flex-1 min-w-0">
        <span className="text-[13px] font-black text-black mr-2">{p.title}</span>
        <span className="text-[11px] text-black/40 font-mono">{p.role}</span>
      </div>

      {/* Tags — desktop only */}
      <div className="hidden sm:flex items-center gap-1.5 shrink-0">
        {p.tags.map(t => (
          <span
            key={t}
            className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide text-black/35 bg-black/4 rounded border border-black/6"
          >
            {t}
          </span>
        ))}
      </div>

      <ArrowRight
        size={13}
        className="shrink-0 text-black/20 group-hover:text-purple-500 group-hover:translate-x-1 transition-all duration-150"
      />
    </motion.a>
  );
}

/* ─── Main section ──────────────────────────────────────────── */
export default function Projects() {
  return (
    <section id="projects" className="relative overflow-x-hidden py-8 sm:py-10 px-3 sm:px-4 lg:px-6 xl:px-8 bg-transparent">
      <div className="relative z-10 max-w-3xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <motion.p {...fadeUp(0)} className="font-mono text-[10px] uppercase tracking-[0.2em] text-purple-600 font-bold mb-1">
            &lt;/Projects&gt;
          </motion.p>
          <motion.h1 {...fadeUp(0.04)} className="text-2xl sm:text-3xl font-black text-black">
            Projects
          </motion.h1>
          <motion.div {...fadeUp(0.08)} className="mt-3 h-px bg-black/10" />
        </div>

        {/* ── Primary: 2-col hero cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          {PRIMARY.map((p, i) => (
            <PrimaryCard key={p.title} p={p} delay={0.05 + i * 0.07} />
          ))}
        </div>

        {/* ── Secondary ── */}
        <div className="mb-6">
          <SecondaryCard p={SECONDARY} />
        </div>

        {/* ── Tertiary rows ── */}
        <div>
          <motion.p {...fadeUp(0.1)} className="text-[9px] font-mono uppercase tracking-widest text-black/25 mb-1">
            More builds
          </motion.p>
          <motion.div {...fadeUp(0.12)} className="h-px bg-black/8 mb-0" />
          {TERTIARY.map((p, i) => (
            <TertiaryRow key={p.title} p={p} delay={0.12 + i * 0.05} />
          ))}
        </div>

      </div>
    </section>
  );
}